/**
 * TripTimeline Component - Visual timeline showing route stops
 */

import React from "react";
import { Timeline, Tag, Card, Typography, Space } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const { Text, Title } = Typography;

const TimelineContainer = styled.div`
  padding: 24px;
  background: #fafafa;
  border-radius: 8px;
`;

const StopCard = styled(Card)`
  margin-bottom: 8px;
  border-radius: 4px;

  .ant-card-body {
    padding: 12px;
  }
`;

const getStatusColor = (status) => {
  const colors = {
    pending: "default",
    arrived: "processing",
    completed: "success",
  };
  return colors[status] || "default";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: <ClockCircleOutlined />,
    arrived: <ClockCircleOutlined />,
    completed: <CheckCircleOutlined />,
  };
  return icons[status] || <ClockCircleOutlined />;
};

const TripTimeline = ({ drops = [], orders = [] }) => {
  if (!drops || drops.length === 0) {
    return (
      <TimelineContainer>
        <Text type="secondary">No drops scheduled for this trip</Text>
      </TimelineContainer>
    );
  }

  // Sort drops by sequence
  const sortedDrops = [...drops].sort((a, b) => a.sequence - b.sequence);

  const getOrdersForDrop = (drop) => {
    if (!drop.orders || !orders) return [];
    return drop.orders
      .map((dropOrder) => {
        const order = Array.isArray(orders)
          ? orders.find((o) => o.id === dropOrder.orderId)
          : null;
        return order;
      })
      .filter(Boolean);
  };

  return (
    <TimelineContainer>
      <Title level={5} style={{ marginBottom: 24 }}>
        Route Timeline
      </Title>
      <Timeline>
        {sortedDrops.map((drop, index) => {
          const dropOrders = getOrdersForDrop(drop);

          return (
            <Timeline.Item
              key={drop.id || index}
              color={
                drop.status === "completed"
                  ? "green"
                  : drop.status === "arrived"
                  ? "blue"
                  : "gray"
              }
            >
              <StopCard>
                <Space direction="vertical" style={{ width: "100%" }} size="small">
                  <Space>
                    <EnvironmentOutlined />
                    <Text strong>
                      {drop.location?.city || "Unknown"},{" "}
                      {drop.location?.province || ""}
                    </Text>
                    <Tag
                      color={getStatusColor(drop.status)}
                      icon={getStatusIcon(drop.status)}
                    >
                      {drop.status?.toUpperCase() || "PENDING"}
                    </Tag>
                  </Space>
                  {drop.location?.address && (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {drop.location.address}
                    </Text>
                  )}
                  {dropOrders.length > 0 && (
                    <div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Orders: {dropOrders.length}
                      </Text>
                      <div style={{ marginTop: 4 }}>
                        {dropOrders.slice(0, 3).map((order) => (
                          <Tag key={order.id} style={{ marginTop: 4 }}>
                            {order.id}
                          </Tag>
                        ))}
                        {dropOrders.length > 3 && (
                          <Tag style={{ marginTop: 4 }}>
                            +{dropOrders.length - 3} more
                          </Tag>
                        )}
                      </div>
                    </div>
                  )}
                  {drop.completedAt && (
                    <Text type="secondary" style={{ fontSize: "11px" }}>
                      Completed: {new Date(drop.completedAt).toLocaleString()}
                    </Text>
                  )}
                </Space>
              </StopCard>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </TimelineContainer>
  );
};

export default TripTimeline;

